import { opfsBlobCache } from '@affine/core/utils/opfs-blob-cache';
import type { SpaceType } from '@affine/nbstore';
import { BLOB_LOCAL_STORAGE_UNAVAILABLE_ERROR_CODE } from '@blocksuite/affine/sync';
import { BRAND_DISPLAY_NAME } from '@streamlain/brand';
import { backoffRetry } from '@toeverything/infra';
import { defer, firstValueFrom } from 'rxjs';

import { workspaceApiUrl } from '../../cloud/services/workspace-api-client';

const BLOB_RANGE_CHUNK_SIZE = 4 * 1024 * 1024;
const LOCAL_STORAGE_UNAVAILABLE_ERROR_NAME =
  'StreamLainBlobLocalStorageUnavailableError';

interface WorkspaceBlobInfo {
  key: string;
  mimeType: string;
  size: number;
  createdAt?: string;
}

export interface WorkspaceBlobMetadata {
  mime: string;
  size: number;
}

type WorkspaceBlobInfoResult =
  | { status: 'ok'; info: WorkspaceBlobInfo }
  | { status: 'terminal' }
  | { status: 'unavailable'; error: WorkspaceBlobRemoteUnavailableError };

export class WorkspaceBlobRemoteUnavailableError extends Error {
  readonly retryable: boolean;

  constructor(
    readonly status?: number,
    override readonly cause?: unknown
  ) {
    super('Failed to retrieve remote file');
    this.name = 'WorkspaceBlobRemoteUnavailableError';
    this.retryable = status === undefined || status === 429 || status >= 500;
  }
}

function remoteErrorStatus(error: unknown): number | undefined {
  if (!error || typeof error !== 'object' || !('status' in error)) return;
  return typeof error.status === 'number' ? error.status : undefined;
}

// Keep retries inside the existing workspace transport: it owns concurrency,
// backend availability and Retry-After backoff. Only these idempotent reads
// repeat, with a finite budget; access denials are never retried.
async function fetchBlobRead(
  fetchImpl: typeof fetch,
  input: string,
  init: RequestInit,
  retry = false
): Promise<Response> {
  return firstValueFrom(
    defer(async () => {
      let response: Response;
      try {
        response = await fetchImpl(input, init);
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError')
          throw error;
        throw new WorkspaceBlobRemoteUnavailableError(
          remoteErrorStatus(error),
          error
        );
      }
      if (response.status === 429 || response.status >= 500) {
        throw new WorkspaceBlobRemoteUnavailableError(response.status);
      }
      return response;
    }).pipe(
      backoffRetry({
        count: retry ? 2 : 0,
        delay: 1000,
        when: error =>
          error instanceof WorkspaceBlobRemoteUnavailableError &&
          error.retryable,
      })
    )
  );
}

/** Exact initialized local storage tuple; API workspaceId remains separate. */
export interface LocalBlobScope {
  readonly flavour: string;
  readonly type: SpaceType;
  readonly id: string;
}

export interface WorkspaceBlobContext {
  readonly local: LocalBlobScope;
  readonly external: boolean;
  assertActive: () => void;
  canReadPending: () => boolean;
}

export function workspaceBlobNamespace(scope: LocalBlobScope): string {
  if (!scope.flavour || !scope.type || !scope.id) {
    throw new Error('Initialized local blob scope required');
  }
  // Local-only workspaces retain their existing device-local file namespace.
  if (scope.flavour === 'local' && scope.type === 'workspace') return scope.id;
  // Reuse the initialized storage tuple, as the existing IDB connection does.
  return `${scope.flavour}:${scope.type}:${scope.id}`;
}

export interface ResolveWorkspaceBlobOptions {
  workspaceId: string;
  key: string;
  fetchImpl?: typeof fetch;
  context: WorkspaceBlobContext;
}

type WorkspaceBlobFetchFactory = (workspaceId: string) => typeof fetch;
let workspaceBlobFetchFactory: WorkspaceBlobFetchFactory | undefined;

export function configureWorkspaceBlobFetchFactory(
  factory: WorkspaceBlobFetchFactory
): void {
  workspaceBlobFetchFactory = factory;
}

export class WorkspaceBlobLocalStorageUnavailableError extends Error {
  readonly code = BLOB_LOCAL_STORAGE_UNAVAILABLE_ERROR_CODE;

  constructor() {
    super(localStorageUnavailableMessage());
    this.name = LOCAL_STORAGE_UNAVAILABLE_ERROR_NAME;
  }
}

export function isWorkspaceBlobLocalStorageUnavailableError(
  error: unknown
): error is WorkspaceBlobLocalStorageUnavailableError {
  return (
    error instanceof Error &&
    (error.name === LOCAL_STORAGE_UNAVAILABLE_ERROR_NAME ||
      (error as { code?: unknown }).code ===
        BLOB_LOCAL_STORAGE_UNAVAILABLE_ERROR_CODE)
  );
}

function getBuildVersionHeaders(): Record<string, string> {
  return typeof BUILD_CONFIG !== 'undefined' && BUILD_CONFIG.appVersion
    ? { 'x-streamlain-version': BUILD_CONFIG.appVersion }
    : {};
}

/** Info of several blobs in one read; the server names at most BLOB_INFO_BATCH_LIMIT keys. */
function workspaceBlobInfosUrl(
  workspaceId: string,
  keys: readonly string[]
): string {
  return `/api/workspaces/${encodeURIComponent(workspaceId)}/blob-infos?keys=${keys.map(encodeURIComponent).join(',')}`;
}

/** The one place that spells the workspace blob endpoint (Range-capable). */
export function workspaceBlobUrl(
  workspaceId: string,
  key: string,
  suffix: '' | '/info' = ''
): string {
  return `/api/workspaces/${encodeURIComponent(workspaceId)}/blobs/${encodeURIComponent(key)}${suffix}`;
}

async function fetchWorkspaceBlobInfo(
  workspaceId: string,
  key: string,
  fetchImpl: typeof fetch,
  retry = false
): Promise<WorkspaceBlobInfoResult> {
  try {
    const response = await fetchBlobRead(
      fetchImpl,
      workspaceBlobUrl(workspaceId, key, '/info'),
      {
        credentials: 'include',
        cache: 'no-store',
        headers: getBuildVersionHeaders(),
      },
      retry
    );
    if (!response.ok) {
      return isTerminalBlobInfoStatus(response.status)
        ? { status: 'terminal' }
        : {
            status: 'unavailable',
            error: new WorkspaceBlobRemoteUnavailableError(response.status),
          };
    }
    const info = parseWorkspaceBlobInfo(await response.json());
    return info
      ? { status: 'ok', info }
      : {
          status: 'unavailable',
          error: new WorkspaceBlobRemoteUnavailableError(response.status),
        };
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError')
      throw error;
    const status = remoteErrorStatus(error);
    // workspaceApiFetch rejects non-2xx responses instead of returning them.
    // Preserve its authorization/missing result across this fetch adapter.
    if (status !== undefined && isTerminalBlobInfoStatus(status))
      return { status: 'terminal' };
    return {
      status: 'unavailable',
      error:
        error instanceof WorkspaceBlobRemoteUnavailableError
          ? error
          : new WorkspaceBlobRemoteUnavailableError(status, error),
    };
  }
}

function parseWorkspaceBlobInfo(body: unknown): WorkspaceBlobInfo | null {
  if (!body || typeof body !== 'object') return null;
  const { key, mimeType, size, createdAt } = body as Partial<WorkspaceBlobInfo>;
  if (
    typeof key !== 'string' ||
    typeof mimeType !== 'string' ||
    typeof size !== 'number' ||
    !Number.isSafeInteger(size) ||
    size < 0
  )
    return null;
  return {
    key,
    mimeType,
    size,
    ...(typeof createdAt === 'string' ? { createdAt } : {}),
  };
}

/** Info of several blobs in one read (GET /api/workspaces/:id/blob-infos). */
async function fetchWorkspaceBlobInfos(
  workspaceId: string,
  keys: readonly string[],
  fetchImpl: typeof fetch
): Promise<Map<string, WorkspaceBlobInfoResult>> {
  const results = new Map<string, WorkspaceBlobInfoResult>();
  const all = (result: WorkspaceBlobInfoResult) => {
    for (const key of keys) results.set(key, result);
    return results;
  };
  try {
    const response = await fetchBlobRead(
      fetchImpl,
      workspaceBlobInfosUrl(workspaceId, keys),
      {
        credentials: 'include',
        cache: 'no-store',
        headers: getBuildVersionHeaders(),
      }
    );
    // A server without blob-infos answers 404; a missing blob is a null entry.
    if (!response.ok)
      return all(
        response.status !== 404 && isTerminalBlobInfoStatus(response.status)
          ? { status: 'terminal' }
          : {
              status: 'unavailable',
              error: new WorkspaceBlobRemoteUnavailableError(response.status),
            }
      );
    const body = (await response.json()) as {
      blobs?: Record<string, unknown>;
    };
    for (const key of keys) {
      const entry = body.blobs?.[key];
      // null: missing or not readable, as 404/403 of the single info route.
      if (entry === null) {
        results.set(key, { status: 'terminal' });
        continue;
      }
      const info = parseWorkspaceBlobInfo(entry);
      results.set(
        key,
        info && info.key === key
          ? { status: 'ok', info }
          : {
              status: 'unavailable',
              error: new WorkspaceBlobRemoteUnavailableError(response.status),
            }
      );
    }
    return results;
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError')
      throw error;
    const status = remoteErrorStatus(error);
    return all(
      status !== undefined && status !== 404 && isTerminalBlobInfoStatus(status)
        ? { status: 'terminal' }
        : {
            status: 'unavailable',
            error:
              error instanceof WorkspaceBlobRemoteUnavailableError
                ? error
                : new WorkspaceBlobRemoteUnavailableError(status, error),
          }
    );
  }
}

function isTerminalBlobInfoStatus(status: number): boolean {
  return (
    status === 400 ||
    status === 401 ||
    status === 403 ||
    status === 404 ||
    status === 410
  );
}

export function workspaceBlobCacheToken(key: string, size: number): string {
  return `${key}:${size}`;
}

function cacheTokenForWorkspaceBlobInfo(info: WorkspaceBlobInfo): string {
  return workspaceBlobCacheToken(info.key, info.size);
}

function restoreBlobMimeType(blob: Blob, mime: string | undefined): Blob {
  const normalizedMime = mime?.trim();
  if (blob.type || !normalizedMime) return blob;
  return blob.slice(0, blob.size, normalizedMime);
}

function localStorageUnavailableMessage(): string {
  const details = opfsBlobCache.getAvailability();
  const base = `Local browser storage is blocked. ${BRAND_DISPLAY_NAME} needs site data to load images and PDFs.`;
  const isFirefox =
    typeof navigator !== 'undefined' && /firefox/i.test(navigator.userAgent);
  const firefoxHint = isFirefox
    ? ` In Firefox, leave private or no-history mode, allow site data for ${BRAND_DISPLAY_NAME}, then reload.`
    : ` Allow site data for ${BRAND_DISPLAY_NAME}, then reload.`;
  const reason =
    details.available || !details.reason ? '' : ` (${details.reason})`;
  return `${base}${firefoxHint}${reason}`;
}

function assertOPFSAvailable(): void {
  if (!opfsBlobCache.available) {
    throw new WorkspaceBlobLocalStorageUnavailableError();
  }
}

const remoteValidations = new Set<string>();

function remoteValidationKey(workspaceId: string, key: string): string {
  return `${workspaceId}:${key}`;
}

/** Keys one blob-infos read may name; the server refuses more (BLOB_INFO_BATCH_LIMIT). */
const BLOB_INFO_BATCH_LIMIT = 100;
/** Cache hits of one start arrive one after another; they are checked together. */
const VALIDATION_BATCH_DELAY_MS = 250;

type ValidationBatch = {
  workspaceId: string;
  fetchImpl: typeof fetch;
  context: WorkspaceBlobContext;
  keys: string[];
};
const validationBatches = new Map<string, ValidationBatch>();

/**
 * Checks a cached blob against the server in the background: deleted or no
 * longer readable blobs and stale bytes leave the cache. The cache hits of a
 * namespace are checked together in one blob-infos read.
 */
function validateCachedBlobInBackground(
  workspaceId: string,
  key: string,
  fetchImpl: typeof fetch,
  context: WorkspaceBlobContext
): void {
  const namespace = workspaceBlobNamespace(context.local);
  const validationKey = remoteValidationKey(namespace, key);
  if (remoteValidations.has(validationKey)) return;
  remoteValidations.add(validationKey);

  let batch = validationBatches.get(namespace);
  // A new context of the same namespace (identity change) is checked on its own.
  if (batch && batch.context !== context) {
    flushValidationBatch(namespace);
    batch = undefined;
  }
  if (!batch) {
    batch = { workspaceId, fetchImpl, context, keys: [] };
    validationBatches.set(namespace, batch);
    setTimeout(
      () => flushValidationBatch(namespace),
      VALIDATION_BATCH_DELAY_MS
    );
  }
  batch.keys.push(key);
  if (batch.keys.length >= BLOB_INFO_BATCH_LIMIT)
    flushValidationBatch(namespace);
}

function flushValidationBatch(namespace: string): void {
  const batch = validationBatches.get(namespace);
  if (!batch) return;
  validationBatches.delete(namespace);
  const { workspaceId, fetchImpl, context, keys } = batch;
  void validateCachedBlobs(namespace, workspaceId, keys, fetchImpl, context)
    .catch(() => undefined)
    .finally(() => {
      for (const key of keys)
        remoteValidations.delete(remoteValidationKey(namespace, key));
    });
}

async function validateCachedBlobs(
  namespace: string,
  workspaceId: string,
  keys: readonly string[],
  fetchImpl: typeof fetch,
  context: WorkspaceBlobContext
): Promise<void> {
  context.assertActive();
  const uploaded: string[] = [];
  for (const key of keys) {
    // A remote miss is expected until the durable upload outbox completes.
    // OPFS is authoritative during that window and must not be invalidated.
    const info = await opfsBlobCache.getInfo(namespace, key).catch(() => null);
    if (info?.uploadState !== 'pending') uploaded.push(key);
  }
  if (uploaded.length === 0) return;
  const results = await fetchWorkspaceBlobInfos(
    workspaceId,
    uploaded,
    fetchImpl
  );
  for (const [key, result] of results) {
    context.assertActive();
    // One unreadable cache entry does not stop the check of the others.
    await applyBlobValidation(namespace, key, result).catch(() => undefined);
  }
}

async function applyBlobValidation(
  namespace: string,
  key: string,
  result: WorkspaceBlobInfoResult
): Promise<void> {
  if (result.status === 'terminal') {
    await opfsBlobCache.delete(namespace, key);
    return;
  }
  if (result.status !== 'ok') return;
  const validated = await opfsBlobCache.getCompleteFile(namespace, key, {
    size: result.info.size,
    cacheToken: cacheTokenForWorkspaceBlobInfo(result.info),
  });
  if (!validated) await opfsBlobCache.delete(namespace, key);
}

function remoteRangeFetcher(
  workspaceId: string,
  key: string,
  fetchImpl: typeof fetch,
  context: WorkspaceBlobContext,
  onError: (error: unknown) => void
): (start: number, endInclusive: number) => Promise<Response> {
  return async (start, endInclusive) => {
    context.assertActive();
    try {
      const response = await fetchBlobRead(
        fetchImpl,
        workspaceBlobUrl(workspaceId, key),
        {
          credentials: 'include',
          // OPFS owns the byte cache. Bypass old HTTP error entries as well
          // as rechecking the current document grant on every remote read.
          cache: 'no-store',
          headers: {
            ...getBuildVersionHeaders(),
            Range: `bytes=${start}-${endInclusive}`,
          },
        },
        true
      );
      if (!response.ok)
        onError(new WorkspaceBlobRemoteUnavailableError(response.status));
      return response;
    } catch (error) {
      onError(error);
      throw error;
    }
  };
}

async function cacheRemoteBlobIntoOPFS(
  workspaceId: string,
  key: string,
  fetchImpl: typeof fetch,
  context: WorkspaceBlobContext,
  info?: WorkspaceBlobInfo
): Promise<File | null> {
  context.assertActive();
  const namespace = workspaceBlobNamespace(context.local);
  const remoteInfoResult =
    info !== undefined
      ? ({ status: 'ok', info } as const)
      : await fetchWorkspaceBlobInfo(workspaceId, key, fetchImpl);
  if (remoteInfoResult.status === 'unavailable') throw remoteInfoResult.error;
  if (remoteInfoResult.status !== 'ok') return null;
  const remoteInfo = remoteInfoResult.info;
  if (!remoteInfo) return null;

  assertOPFSAvailable();
  let rangeError: unknown;
  try {
    const file = await opfsBlobCache.ensureCompleteFromRemote(namespace, key, {
      mime: remoteInfo.mimeType,
      size: remoteInfo.size,
      cacheToken: cacheTokenForWorkspaceBlobInfo(remoteInfo),
      source: 'range',
      chunkSize: BLOB_RANGE_CHUNK_SIZE,
      fetchRange: remoteRangeFetcher(
        workspaceId,
        key,
        fetchImpl,
        context,
        error => {
          rangeError = error;
        }
      ),
    });
    context.assertActive();
    assertOPFSAvailable();
    if (!file) {
      const status = remoteErrorStatus(rangeError);
      if (status !== undefined && isTerminalBlobInfoStatus(status)) return null;
      throw rangeError ?? new WorkspaceBlobRemoteUnavailableError(200);
    }
    return file;
  } catch (error) {
    assertOPFSAvailable();
    throw error;
  }
}

export async function resolveWorkspaceBlobFromCacheOrRemote({
  workspaceId,
  key,
  fetchImpl,
  context,
}: ResolveWorkspaceBlobOptions): Promise<Blob | null> {
  context.assertActive();
  const namespace = workspaceBlobNamespace(context.local);
  const remoteFetch = fetchImpl ?? workspaceBlobFetchFactory?.(workspaceId);
  const localInfo = await opfsBlobCache.getInfo(namespace, key);
  context.assertActive();
  const ownPending =
    localInfo?.uploadState === 'pending' && context.canReadPending();
  // Members keep their existing scoped offline contract. External synced bytes
  // must pass current actor/blob authorization before any cached File escapes.
  if (!context.external || ownPending) {
    const localFile = await opfsBlobCache.getCompleteFile(namespace, key);
    context.assertActive();
    if (localFile) {
      if (remoteFetch && !context.external) {
        validateCachedBlobInBackground(workspaceId, key, remoteFetch, context);
      }
      return restoreBlobMimeType(localFile, localInfo?.mime);
    }
  }

  assertOPFSAvailable();
  if (!remoteFetch) {
    if (context.external) throw new WorkspaceBlobRemoteUnavailableError();
    return null;
  }
  const infoResult = await fetchWorkspaceBlobInfo(
    workspaceId,
    key,
    remoteFetch,
    true
  );
  context.assertActive();
  if (infoResult.status === 'ok') {
    const { info } = infoResult;
    const cachedFile = await opfsBlobCache.getCompleteFile(namespace, key, {
      size: info.size,
      cacheToken: cacheTokenForWorkspaceBlobInfo(info),
    });
    context.assertActive();
    if (cachedFile) return restoreBlobMimeType(cachedFile, info.mimeType);
    const remoteFile = await cacheRemoteBlobIntoOPFS(
      workspaceId,
      key,
      remoteFetch,
      context,
      info
    );
    context.assertActive();
    return remoteFile ? restoreBlobMimeType(remoteFile, info.mimeType) : null;
  }
  if (infoResult.status === 'terminal') {
    await opfsBlobCache.delete(namespace, key);
    return null;
  }
  // External unavailable is never authority to return stale local bytes.
  throw infoResult.error;
}

export async function resolveWorkspaceBlobMetadata({
  workspaceId,
  key,
  fetchImpl,
  context,
}: ResolveWorkspaceBlobOptions): Promise<WorkspaceBlobMetadata | null> {
  context.assertActive();
  const namespace = workspaceBlobNamespace(context.local);
  const localInfo = await opfsBlobCache.getInfo(namespace, key);
  context.assertActive();
  const ownPending =
    localInfo?.uploadState === 'pending' && context.canReadPending();
  if (
    (!context.external || ownPending) &&
    localInfo &&
    typeof localInfo.mime === 'string' &&
    localInfo.mime !== 'application/octet-stream' &&
    Number.isSafeInteger(localInfo.size) &&
    localInfo.size >= 0
  ) {
    return { mime: localInfo.mime, size: localInfo.size };
  }
  const remoteFetch = fetchImpl ?? workspaceBlobFetchFactory?.(workspaceId);
  if (!remoteFetch) {
    if (context.external) throw new WorkspaceBlobRemoteUnavailableError();
    return null;
  }
  const result = await fetchWorkspaceBlobInfo(
    workspaceId,
    key,
    remoteFetch,
    true
  );
  context.assertActive();
  if (result.status === 'unavailable') throw result.error;
  return result.status === 'ok'
    ? { mime: result.info.mimeType, size: result.info.size }
    : null;
}

/** Random access to one workspace blob without holding the whole file. */
export interface WorkspaceBlobRangeReader {
  readonly size: number;
  readonly mime: string;
  /** Bytes [start, endExclusive); disk-backed, fetched chunk-wise on demand. */
  read(start: number, endExclusive: number): Promise<Blob>;
}

/** Range reader over a blob that is already local (File, OPFS copy). */
export function blobRangeReader(
  file: Blob,
  mime = file.type || 'application/octet-stream'
): WorkspaceBlobRangeReader {
  return {
    size: file.size,
    mime,
    read: async (start, endExclusive) => file.slice(start, endExclusive, mime),
  };
}

/**
 * Opens a blob for range reads under the same rules as
 * resolveWorkspaceBlobFromCacheOrRemote: members read complete local copies,
 * external readers always pass the remote info check first, and only the
 * requested chunks are fetched into OPFS.
 */
export async function openWorkspaceBlobRange({
  workspaceId,
  key,
  fetchImpl,
  context,
}: ResolveWorkspaceBlobOptions): Promise<WorkspaceBlobRangeReader | null> {
  context.assertActive();
  const namespace = workspaceBlobNamespace(context.local);
  const remoteFetch = fetchImpl ?? workspaceBlobFetchFactory?.(workspaceId);
  const localInfo = await opfsBlobCache.getInfo(namespace, key);
  context.assertActive();
  const ownPending =
    localInfo?.uploadState === 'pending' && context.canReadPending();
  if (!context.external || ownPending) {
    const localFile = await opfsBlobCache.getCompleteFile(namespace, key);
    context.assertActive();
    if (localFile) {
      if (remoteFetch && !context.external) {
        validateCachedBlobInBackground(workspaceId, key, remoteFetch, context);
      }
      return blobRangeReader(localFile, localInfo?.mime || undefined);
    }
  }

  assertOPFSAvailable();
  if (!remoteFetch) {
    if (context.external) throw new WorkspaceBlobRemoteUnavailableError();
    return null;
  }
  const infoResult = await fetchWorkspaceBlobInfo(
    workspaceId,
    key,
    remoteFetch,
    true
  );
  context.assertActive();
  if (infoResult.status === 'terminal') {
    await opfsBlobCache.delete(namespace, key);
    return null;
  }
  if (infoResult.status === 'unavailable') throw infoResult.error;

  const { info } = infoResult;
  return {
    size: info.size,
    mime: info.mimeType,
    read: async (start, endExclusive) => {
      context.assertActive();
      assertOPFSAvailable();
      let rangeError: unknown;
      const slice = await opfsBlobCache.ensureRangeFromRemote(
        namespace,
        key,
        start,
        endExclusive,
        {
          mime: info.mimeType,
          size: info.size,
          cacheToken: cacheTokenForWorkspaceBlobInfo(info),
          source: 'range',
          chunkSize: BLOB_RANGE_CHUNK_SIZE,
          fetchRange: remoteRangeFetcher(
            workspaceId,
            key,
            remoteFetch,
            context,
            error => {
              rangeError = error;
            }
          ),
        }
      );
      context.assertActive();
      if (!slice) {
        throw rangeError ?? new WorkspaceBlobRemoteUnavailableError(200);
      }
      return slice;
    },
  };
}

/**
 * URL a media element or download link loads directly. Members get a
 * disk-backed object URL when the complete file is already local; otherwise
 * the endpoint URL, which the browser reads with Range requests and which
 * checks access on every request. Local-only workspaces have no endpoint.
 */
export async function resolveWorkspaceBlobStreamUrl({
  workspaceId,
  key,
  context,
}: Omit<ResolveWorkspaceBlobOptions, 'fetchImpl'>): Promise<string | null> {
  context.assertActive();
  const namespace = workspaceBlobNamespace(context.local);
  const localInfo = await opfsBlobCache.getInfo(namespace, key);
  context.assertActive();
  const ownPending =
    localInfo?.uploadState === 'pending' && context.canReadPending();
  if (!context.external || ownPending) {
    const localFile = await opfsBlobCache.getCompleteFile(namespace, key);
    context.assertActive();
    if (localFile) return URL.createObjectURL(localFile);
  }
  if (context.local.flavour === 'local') return null;
  return workspaceApiUrl(workspaceBlobUrl(workspaceId, key));
}
